if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);

        // カラー手数料チェック
        const cldSettings = window.CLD_OPTION_SETTINGS;
        const selectedColor = formData.get('properties[名入れ色]');
        let colorFeeVariantId = null;
        if (cldSettings && selectedColor) {
          const colorEntry = (cldSettings.colors || []).find((c) => c.value === selectedColor);
          if (colorEntry && colorEntry.add > 0 && colorEntry.fee_variant_id) {
            colorFeeVariantId = colorEntry.fee_variant_id;
          }
        }

        let config;
        if (colorFeeVariantId) {
          // JSON モード: 本体 + カラー手数料商品を一括追加
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';

          const properties = {};
          for (const [key, value] of formData.entries()) {
            const m = key.match(/^properties\[(.+)\]$/);
            if (m) properties[m[1]] = value;
          }
          properties['_has_fee'] = '1';

          const mainVariantId = String(parseInt(formData.get('id'), 10));
          const body = {
            items: [
              {
                id: colorFeeVariantId,
                quantity: parseInt(formData.get('quantity') || '1', 10),
                properties: {
                  _fee_for: mainVariantId,
                  _fee_type: 'color',
                  _fee_product_url: window.location.pathname,
                },
              },
              {
                id: parseInt(formData.get('id'), 10),
                quantity: parseInt(formData.get('quantity') || '1', 10),
                properties,
              },
            ],
          };

          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = JSON.stringify(body);
        } else {
          // 既存の FormData モード
          config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        this.removeConflictingLines(formData.get('id'))
          .then(() => fetch(`${routes.cart_add_url}`, config))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      removeConflictingLines(variantId) {
        return fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            const vid = parseInt(variantId, 10);
            const mainLines = cart.items.filter(
              (item) => item.variant_id === vid && !item.properties['_fee_for']
            );
            if (mainLines.length === 0) return;
            const vidStr = String(vid);
            const feeLines = cart.items.filter(
              (item) => item.properties['_fee_for'] === vidStr
            );
            const toRemove = [...mainLines, ...feeLines];
            return toRemove.reduce(
              (p, item) =>
                p.then(() =>
                  fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: item.key, quantity: 0 }),
                  })
                ),
              Promise.resolve()
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
